import { SelectChangeEvent } from "@mui/material";
import { useFormikContext } from "formik";
import {
  FocusEventHandler,
  KeyboardEventHandler,
  ReactEventHandler,
  useCallback,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { SegmentedMessage } from "sms-segments-calculator";
import { Editor } from "tinymce";
import { CampaignContact } from "../../App/Enums/CampaignTypes";
import { useUpsertMessageTemplate } from "../../App/Queries/MessageQueries";
import { useDebouncedHandler } from "../../App/State/Hooks/useDebouncedHandler";
import {
  CloseDialogAction,
  ICampaignFormState,
  IWelcomeMessageActions,
  IWelcomeMessageModalState,
  IWelcomeMessageState,
  WelcomeMessageActiveElementAction,
  WelcomeMessageBaseAction,
  WelcomeMessageEditAction,
  WelcomeMessageEmptyAction,
  WelcomeMessageReducer,
  WelcomeMessageStringAction,
  WelcomeMessageToggleAction,
} from "../../App/Typings/Campaigns";
import { IEmailTemplate, ITextTemplate } from "../../App/Typings/MessageText";
import {
  getMaxMessageError,
  getMaxMessageLength,
  maxWelcomeSubjectError,
  maxWelcomeSubjectLength,
  minTextSegmentThreshold,
  minWelcomeMessageLengthError,
  minWelcomeSubjectLengthError,
} from "../Schema/CampaignFormSchema";

export const useWelcomeMessageModalState = (): IWelcomeMessageModalState => {
  const {
    formFields,
    originalWelcomeMessage,
    originalWelcomeSubject,
    isEmail,
    saveEditedField,
    validateForm,
  } = useExistingFormValues();
  const { mutateAsync: upsertMessageTemplate, isLoading: upsertLoading } = useUpsertMessageTemplate();
  const location = useLocation();
  const [editor, setEditor] = useState<Editor | undefined>();

  const initialWelcomeMessageState: IWelcomeMessageState = useMemo(
    () => ({
      open: false,
      activeElement: null,
      welcomeMessage: originalWelcomeMessage || "",
      welcomeMessageSubject: originalWelcomeSubject || "",
      welcomeMessageTouched: false,
      messageLengthErrorMessage: "",
      subjectLengthErrorMessage: "",
      errorMessages: new Set<string>(),
      templateOpen: false,
      saveTemplateActive: false,
      templateName: "",
      templateVisibility: "Public",
      templateErrorMessage: "",
    }),
    [originalWelcomeMessage, originalWelcomeSubject],
  );

  const reset: WelcomeMessageEmptyAction = useCallback(() => {
    return initialWelcomeMessageState;
  }, [initialWelcomeMessageState]);

  const welcomeMessageDictionary: IWelcomeMessageActions = useMemo(
    () => ({
      setWelcomeMessage,
      setWelcomeSubject,
      setActiveElement,
      setMessageLengthError,
      setSubjectLengthError,
      openDialog,
      closeDialog,
      openTemplateDialog,
      closeTemplateDialog,
      setSaveTemplateActive,
      setTemplateName,
      setTemplateVisibility,
      reset,
    }),
    [reset],
  );

  const welcomeMessageReducer: WelcomeMessageReducer = useCallback(
    (state, action) => {
      if (action.type in welcomeMessageDictionary) {
        return (welcomeMessageDictionary as any)[action.type](state, action.payload);
      }
      return state;
    },
    [welcomeMessageDictionary],
  );

  const [messageState, dispatchMessageAction] = useReducer(welcomeMessageReducer, initialWelcomeMessageState);

  const emptyMessage = !messageState.welcomeMessage;
  const emptySubject = !messageState.welcomeMessageSubject;
  const messageLengthErrorExists = !!messageState.messageLengthErrorMessage;
  const subjectLengthErrorExists = !!messageState.subjectLengthErrorMessage;

  const disableSave =
    emptyMessage || messageLengthErrorExists || (isEmail && (emptySubject || subjectLengthErrorExists));

  const handleEditWelcomeMessage: ReactEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    dispatchMessageAction({
      type: "setWelcomeMessage",
      payload: { welcomeMessage: e.currentTarget.value, isEmail },
    });
  };

  const handleEditWelcomeSubject: ReactEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    dispatchMessageAction({ type: "setWelcomeSubject", payload: e.currentTarget.value });
  };

  const handleSubmitFromEnter: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.ctrlKey && e.key === "Enter") {
      handleSaveChanges();
    }
  };

  const handleFocusInput: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    dispatchMessageAction({ type: "setActiveElement", payload: e.currentTarget });
  };

  const insertAtCursor = (myField: Editor | undefined, myValue: string) => {
    let val = myValue;
    const pos = myField?.selection.getRng();

    if (pos) {
      const content = myField?.getContent({ format: "text" }).trim() ?? "";
      const startPos = pos.startOffset;

      val = content.substring(0, startPos) + myValue + content.substring(startPos, content.length);
    }

    return val;
  };

  const handleAddWelcomeMessageTemplateField = (field: string, isEmail: boolean, isEmailSubject: boolean) => {
    const { activeElement, welcomeMessage, welcomeMessageSubject } = messageState;

    if (isEmail) {
      if (isEmailSubject) {
        dispatchMessageAction({
          type: "setWelcomeSubject",
          payload: `${welcomeMessageSubject} [[${field}]]`,
        });
      } else {
        dispatchMessageAction({
          type: "setWelcomeMessage",
          payload: { welcomeMessage: insertAtCursor(editor, `[[${field}]]`), isEmail },
        });
      }
    } else {
      dispatchMessageAction({
        type: "setWelcomeMessage",
        payload: {
          welcomeMessage: `${welcomeMessage} [[${field}]]`,
          isEmail,
        },
      });
    }
    activeElement?.focus();
  };

  const handleClose: CloseDialogAction = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    dispatchMessageAction({ type: "closeDialog" });
    // wait for animation to finish before resetting state
    setTimeout(() => {
      dispatchMessageAction({ type: "reset" });
    }, 60);
  };

  const handleSaveChanges = () => {
    if (disableSave) {
      return;
    }
    saveEditedField("welcomeMessage", messageState.welcomeMessage);
    if (isEmail) {
      saveEditedField("welcomeMessageSubject", messageState.welcomeMessageSubject);
    }
    dispatchMessageAction({ type: "closeDialog" });
    // wait for animation to finish before resetting state
    setTimeout(async () => {
      // validate form to show/hide errors immediately
      await validateForm();
      dispatchMessageAction({ type: "reset" });
    }, 60);
  };

  const handleOpenDialog = () => {
    dispatchMessageAction({ type: "openDialog", payload: isEmail });
  };

  const handleOpenTemplateDialog = () => {
    dispatchMessageAction({ type: "openTemplateDialog" });
  };

  const handleCloseTemplateDialog = () => {
    dispatchMessageAction({ type: "closeTemplateDialog" });
  };

  const handleTemplateSelected = (template: IEmailTemplate | ITextTemplate) => {
    dispatchMessageAction({
      type: "setWelcomeMessage",
      payload: { welcomeMessage: template.body, isEmail },
    });
    if (isEmail && "subject" in template) {
      dispatchMessageAction({ type: "setWelcomeSubject", payload: template.subject });
    }
    dispatchMessageAction({ type: "closeTemplateDialog" });
  };

  const handleShowSaveTemplate = () => {
    dispatchMessageAction({ type: "setSaveTemplateActive", payload: true });
  };

  const handleHideSaveTemplate = () => {
    dispatchMessageAction({ type: "setSaveTemplateActive", payload: false });
  };

  const handleTemplateNameChange: ReactEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    dispatchMessageAction({ type: "setTemplateName", payload: e.currentTarget.value });
  };

  const handleTemplateVisibilityChange = (e: SelectChangeEvent<string>) => {
    dispatchMessageAction({ type: "setTemplateVisibility", payload: e.target.value });
  };

  const handleSaveEmailTemplate = useDebouncedHandler(async () => {
    if (upsertLoading) {
      return;
    }
    await upsertMessageTemplate(
      {
        name: messageState.templateName,
        body: messageState.welcomeMessage,
        subject: messageState.welcomeMessageSubject,
        visibility: messageState.templateVisibility,
        uiComponent: location.pathname,
        type: "email",
      },
      {
        onSuccess: () => {
          dispatchMessageAction({ type: "setSaveTemplateActive", payload: false });
          dispatchMessageAction({ type: "setTemplateName", payload: "" });
          dispatchMessageAction({ type: "setTemplateVisibility", payload: "Public" });
        },
      },
    );
  }, 300);

  const handleSaveTextTemplate = useDebouncedHandler(async () => {
    if (upsertLoading) {
      return;
    }
    await upsertMessageTemplate(
      {
        name: messageState.templateName,
        body: messageState.welcomeMessage,
        subject: messageState.welcomeMessageSubject,
        visibility: messageState.templateVisibility,
        uiComponent: location.pathname,
        type: "text",
      },
      {
        onSuccess: () => {
          dispatchMessageAction({ type: "setSaveTemplateActive", payload: false });
          dispatchMessageAction({ type: "setTemplateName", payload: "" });
          dispatchMessageAction({ type: "setTemplateVisibility", payload: "Public" });
        },
      },
    );
  }, 300);

  const generateButtonTooltipMessage = useCallback(() => {
    if (messageState.errorMessages.size > 0) {
      return Array.from(messageState.errorMessages).join(", ");
    }
    const errors = new Set<string>();
    if (messageState.welcomeMessage.length === 0) {
      errors.add(minWelcomeMessageLengthError);
    }
    if (messageState.welcomeMessageSubject.length === 0 && isEmail) {
      errors.add(minWelcomeSubjectLengthError);
    }
    if (errors.size > 0) {
      return Array.from(errors).join(", ");
    }
    return "Save Changes (Ctrl + Enter)";
  }, [isEmail, messageState.errorMessages, messageState.welcomeMessage, messageState.welcomeMessageSubject]);

  return {
    getters: {
      formFields,
      isEmail,
      messageState,
      disableSave,
      emptyMessage,
      messageLengthErrorExists,
      emptySubject,
      subjectLengthErrorExists,
    },
    handlers: {
      handleEditWelcomeMessage,
      handleEditWelcomeSubject,
      handleSubmitFromEnter,
      handleOpenDialog,
      handleClose,
      handleOpenTemplateDialog,
      handleCloseTemplateDialog,
      handleTemplateSelected,
      handleShowSaveTemplate,
      handleHideSaveTemplate,
      handleSaveEmailTemplate,
      handleSaveTextTemplate,
      handleTemplateNameChange,
      handleTemplateVisibilityChange,
      handleSaveChanges,
      handleFocusInput,
      handleAddWelcomeMessageTemplateField,
      generateButtonTooltipMessage,
    },
    setters: {
      setEditor,
    },
  };
};

const setWelcomeMessage: WelcomeMessageEditAction = (state, payload) => {
  const maxMessageLength = getMaxMessageLength(payload.isEmail);
  const maxMessageLengthError = getMaxMessageError(payload.isEmail);
  // CRM-485 Remove max length validation for Email messages until Large Email Templates are implemented
  // const messageTooLong = payload.welcomeMessage.length > maxMessageLength;
  const messageTooLong = !payload.isEmail && payload.welcomeMessage.length > maxMessageLength;
  const messageTooShort = payload.welcomeMessage.length === 0;
  const messageTextSplit = !payload.isEmail && payload.welcomeMessage.length >= minTextSegmentThreshold;

  if (!messageTooLong) {
    state.errorMessages?.delete(maxMessageLengthError);
  }
  if (!messageTooShort) {
    state.errorMessages?.delete(minWelcomeMessageLengthError);
  }

  if (messageTextSplit) {
    const estimatedSegments = new SegmentedMessage(payload.welcomeMessage.trim()).segmentsCount;
    if (estimatedSegments > 1) {
      const splitMessageWarning = `Your message will be split into ${estimatedSegments} messages, each will be treated as a separate text message in your quota.`;
      state.textSplitCount = estimatedSegments;
      state.textSplitWarning = splitMessageWarning;
    }
  } else {
    state.textSplitCount = undefined;
    state.textSplitWarning = undefined;
  }

  return {
    ...state,
    welcomeMessage: payload.welcomeMessage,
    messageLengthErrorMessage: state.welcomeMessageTouched
      ? messageTooLong
        ? maxMessageLengthError
        : messageTooShort
          ? minWelcomeMessageLengthError
          : ""
      : "",
    errorMessages: state.welcomeMessageTouched
      ? messageTooLong
        ? state.errorMessages.add(maxMessageLengthError)
        : messageTooShort
          ? state.errorMessages.add(minWelcomeMessageLengthError)
          : state.errorMessages
      : state.errorMessages,
    welcomeMessageTouched: true,
  };
};

const setWelcomeSubject: WelcomeMessageStringAction = (state, payload) => {
  const subjectTooLong = payload.length > maxWelcomeSubjectLength;
  const subjectTooShort = payload.length === 0;

  const subjectLengthErrorMessage = subjectTooLong
    ? maxWelcomeSubjectError
    : subjectTooShort
      ? minWelcomeSubjectLengthError
      : "";

  const errorMessages = subjectTooLong
    ? state.errorMessages.add(maxWelcomeSubjectError)
    : subjectTooShort
      ? state.errorMessages.add(minWelcomeSubjectLengthError)
      : state.errorMessages;

  if (!subjectTooShort && !subjectTooLong) {
    state.errorMessages?.delete(maxWelcomeSubjectError);
    state.errorMessages?.delete(minWelcomeSubjectLengthError);
  }

  return {
    ...state,
    welcomeMessageSubject: payload,
    subjectLengthErrorMessage,
    errorMessages,
  };
};

const setActiveElement: WelcomeMessageActiveElementAction = (state, payload) => {
  return { ...state, activeElement: payload };
};

const setMessageLengthError: WelcomeMessageStringAction = (state, payload) => {
  return { ...state, messageLengthErrorMessage: payload };
};

const setSubjectLengthError: WelcomeMessageStringAction = (state, payload) => {
  return { ...state, subjectLengthErrorMessage: payload };
};

const openDialog: WelcomeMessageToggleAction = (state, isEmail) => {
  if (isEmail) {
    // if Email and the user has edited the message, but not the subject, we should clear the message assuming they have edited the message as an SMS and then changed the campaign type to Email
    if (state.welcomeMessage.length && !state.welcomeMessageSubject.length) {
      state.welcomeMessage = "";
    }
  } else {
    // if Text and the user has edited the message and subject, we should clear both the message and the subject assuming they have edited the message as an Email and then changed the campaign type to SMS
    if (state.welcomeMessage.length && state.welcomeMessageSubject.length) {
      state.welcomeMessage = "";
      state.welcomeMessageSubject = "";
    }
  }
  return { ...state, open: true };
};

const closeDialog: WelcomeMessageBaseAction = (state) => {
  return { ...state, open: false };
};

const openTemplateDialog: WelcomeMessageBaseAction = (state) => {
  return { ...state, templateOpen: true };
};

const closeTemplateDialog: WelcomeMessageBaseAction = (state) => {
  return { ...state, templateOpen: false };
};

const setSaveTemplateActive: WelcomeMessageToggleAction = (state, isSaveActionActive) => {
  return { ...state, saveTemplateActive: isSaveActionActive };
};

const setTemplateName: WelcomeMessageStringAction = (state, payload) => {
  const templateNameError = "Template name is required.";
  if (payload === "") {
    state.templateErrorMessage = templateNameError;
    state.errorMessages?.add(templateNameError);
  } else {
    state.templateErrorMessage = "";
    state.errorMessages?.delete(templateNameError);
  }
  return { ...state, templateName: payload };
};

const setTemplateVisibility: WelcomeMessageStringAction = (state, payload) => {
  return { ...state, templateVisibility: payload };
};

/**
 * Hook to retrieve form values and provide functions to edit form values and fields
 *
 * @returns Object containing original welcome message, original welcome subject,
 * a flag indicating if the selected contact preference is email, and a `saveEditedField` function
 * to set new values and validate specific form fields.
 */
const useExistingFormValues = () => {
  const {
    values: { welcomeMessage, welcomeMessageSubject, contactPreference, formFields },
    handleChange,
    validateField,
    validateForm,
  } = useFormikContext<ICampaignFormState>();

  const saveEditedField = useCallback(
    (fieldName: string, value: string) => {
      if (fieldName === "welcomeMessage" || fieldName === "welcomeMessageSubject") {
        handleChange({ target: { name: fieldName, value } });
        validateField(fieldName);
      }
    },
    [handleChange, validateField],
  );

  return useMemo(
    () => ({
      formFields,
      originalWelcomeMessage: welcomeMessage,
      originalWelcomeSubject: welcomeMessageSubject,
      isEmail: contactPreference?.includes(CampaignContact.Email),
      saveEditedField,
      validateForm,
    }),
    [formFields, welcomeMessage, welcomeMessageSubject, contactPreference, saveEditedField, validateForm],
  );
};
